// SecondWingCelebration — full-screen ceremonial overlay shown ONCE when
// the user crosses the 30-day momentum-threshold barrier and earns their
// second WYNG wing.
//
// Sequence (≈4.5s total):
//   0.0s  Black-out fade in
//   0.3s  Single wing rises into view
//   1.2s  Eyebrow text fades in: "Your second wing has been earned"
//   1.8s  Second wing materialises beside the first with shimmer
//   2.6s  Body copy fades in: "You are no longer becoming."
//   3.6s  Tap-to-continue prompt
//
// We keep particles minimal — this moment is about gravitas, not noise.

import { useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import WyngWingMark from "@/components/WyngWingMark";
import { useLoyalty } from "@/contexts/LoyaltyProvider";

const COPY = {
    nl: {
        eyebrow: "JE TWEEDE WYNG",
        line1:   "Je tweede wing is verdiend.",
        line2:   "Je bent niet langer aan het worden.",
        line3:   "Je BENT.",
        cta:     "Tik om verder te gaan",
    },
    en: {
        eyebrow: "YOUR SECOND WYNG",
        line1:   "Your second wing has been earned.",
        line2:   "You are no longer becoming.",
        line3:   "You ARE.",
        cta:     "Tap to continue",
    },
};


export default function SecondWingCelebration() {
    const { celebrate, dismissCelebrate } = useLoyalty();
    const { i18n } = useTranslation();
    const { pathname } = useLocation();
    const lang = i18n.language?.startsWith("nl") ? "nl" : "en";
    const t = COPY[lang];

    // Suppress the celebration on focused-mode routes where it would
    // hijack the user's first tap (e.g. Winkelmodus). It will fire the
    // next time the user lands on a "home" surface (dashboard etc.).
    const FOCUSED_ROUTE = /^\/(grocery\/[^/]+\/shop|battles\/[^/]+|onboarding|register|login|promo)/;
    const suppress = FOCUSED_ROUTE.test(pathname);
    const visible = celebrate && !suppress;

    // Haptic burst on iOS when the second wing materialises (1.8s mark).
    useEffect(() => {
        if (!visible) return;
        const tick = setTimeout(() => {
            try {
                if (navigator.vibrate) navigator.vibrate([12, 40, 24]);
            } catch {}
        }, 1800);
        return () => clearTimeout(tick);
    }, [visible]);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="second-wing"
                    role="dialog"
                    aria-modal="true"
                    data-testid="second-wing-celebration"
                    onClick={dismissCelebrate}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="fixed inset-0 z-[300] flex items-center justify-center cursor-pointer"
                    style={{
                        background: "radial-gradient(ellipse at center, #0B0B0D 0%, #000 100%)",
                    }}>

                    {/* Soft accent halo */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 0.6, scale: 1 }}
                        transition={{ duration: 1.6, ease: "easeOut" }}
                        className="absolute h-[420px] w-[420px] rounded-full pointer-events-none"
                        style={{
                            background: "radial-gradient(circle, var(--wyng-accent) 0%, transparent 65%)",
                            filter: "blur(40px)",
                            opacity: 0.45,
                        }} />

                    <div className="relative z-10 px-6 text-center max-w-sm">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.1, duration: 0.6 }}
                            className="text-[10px] uppercase tracking-[0.3em] font-bold mb-6"
                            style={{ color: "var(--wyng-accent)" }}>
                            {t.eyebrow}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.6, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 1.0, ease: [0.25, 0.5, 0.25, 1] }}
                            className="flex justify-center mb-8">
                            {/* Left wing first, then both wings cross-fade */}
                            <SequencedWings />
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.6, duration: 0.7 }}
                            className="font-display text-2xl font-semibold tracking-tighter leading-tight">
                            {t.line2}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 3.0, duration: 0.7 }}
                            className="font-display text-3xl font-extrabold tracking-tighter mt-1"
                            style={{ color: "var(--wyng-accent)" }}>
                            {t.line3}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.55 }}
                            transition={{ delay: 3.6, duration: 0.6 }}
                            className="text-[11px] mt-10 italic">
                            {t.cta}
                        </motion.p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}


/* Cross-fades from a single wing (1) to two wings (2) at the 1.8s mark. */
function SequencedWings() {
    return (
        <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, -3, 3, 0] }}
            transition={{ delay: 1.6, duration: 0.9 }}
            className="relative">
            {/* Single wing — fades OUT at 1.6s */}
            <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ delay: 1.6, duration: 0.4 }}
                className="absolute inset-0 flex items-center justify-center">
                <WyngWingMark wings={1} size={120} glow={true} animate={false} />
            </motion.div>
            {/* Both wings — fade IN at 1.8s */}
            <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.8, duration: 0.7 }}>
                <WyngWingMark wings={2} size={120} glow={true} animate={true} />
            </motion.div>
        </motion.div>
    );
}
